import { Component, Input, Output, EventEmitter, HostListener } from '@angular/core';

@Component({
  selector: 'app-modal',
  standalone: false,
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.css']
})
export class ModalComponent {
  @Input() isOpen = false;
  @Input() title = '';
  @Input() size: 'sm' | 'md' | 'lg' | 'xl' | '2xl' = 'md';
  @Input() showFooter = false;

  /**
   * Modo formulario largo (opt-in, no cambia el comportamiento por defecto).
   *
   * Con `false` el modal crece hacia abajo y el que hace scroll es la página, así que
   * en un formulario alto el título se pierde arriba y en móvil queda inmanejable.
   * Con `true` el panel se limita al alto de la ventana, el encabezado y el pie quedan
   * fijos y solo scrollea el contenido; en móvil ocupa la pantalla completa.
   */
  @Input() adaptable = false;

  @Output() closed = new EventEmitter<void>();

  @HostListener('document:keydown.escape') onKeydownHandler() {
    if (this.isOpen) {
      this.close();
    }
  }

  getSizeClass(): string {
    const sizes = {
      sm: 'sm:max-w-sm',
      md: 'sm:max-w-md',
      lg: 'sm:max-w-lg',
      xl: 'sm:max-w-3xl',
      '2xl': 'sm:max-w-5xl'
    };
    return sizes[this.size];
  }

  // ── Clases por modo ───────────────────────────────────────────────────────
  // El modo clásico reproduce exactamente el markup anterior, para no alterar
  // ninguna de las pantallas que ya usaban este componente.

  contenedorClases(): string {
    return this.adaptable
      // Móvil: anclado abajo (hoja inferior). Escritorio: centrado.
      // `pointer-events-none` deja pasar el clic al fondo para poder cerrar tocando fuera.
      ? 'fixed inset-0 flex items-end justify-center sm:items-center sm:p-4 pointer-events-none'
      : 'flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0';
  }

  panelClases(): string {
    // En móvil NO ocupa el 100% de alto: se deja ver una franja del fondo oscurecido
    // arriba para que se lea como una capa sobre la página y no como otra pantalla.
    // Además crece solo lo que necesite el contenido, hasta el tope.
    const base = this.adaptable
      ? 'relative flex flex-col w-full bg-white shadow-2xl pointer-events-auto max-h-[92vh] sm:max-h-[85vh] rounded-t-2xl sm:rounded-2xl overflow-hidden'
      : 'inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full mx-auto';
    return `${base} ${this.getSizeClass()}`;
  }

  encabezadoClases(): string {
    return this.adaptable
      ? 'flex justify-between items-start gap-3 px-4 sm:px-6 py-4 border-b border-slate-100 shrink-0'
      : 'bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 flex justify-between items-start border-b border-slate-100';
  }

  cuerpoClases(): string {
    // En modo adaptable es el ÚNICO elemento que scrollea: el encabezado y el pie
    // quedan siempre visibles, que es lo que hace usable un formulario largo en móvil.
    return this.adaptable
      ? 'flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4'
      : 'bg-white px-4 pt-5 pb-4 sm:p-6';
  }

  tituloClases(): string {
    return this.adaptable
      ? 'text-base sm:text-lg leading-6 font-semibold text-slate-900 min-w-0'
      : 'text-lg leading-6 font-medium text-slate-900';
  }

  cerrarClases(): string {
    return this.adaptable
      ? '-mr-1 -mt-1 p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus:outline-none transition-colors shrink-0'
      : 'bg-white rounded-md text-slate-400 hover:text-slate-500 focus:outline-none';
  }

  pieClases(): string {
    return this.adaptable
      ? 'px-4 sm:px-6 py-3 bg-slate-50 border-t border-slate-100 shrink-0'
      : 'bg-slate-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse';
  }

  close() {
    this.isOpen = false;
    this.closed.emit();
  }
}
